import {
  Package, Zap, Truck, Settings,
  Activity, Wrench, Recycle
} from 'lucide-react';

// Category configurations with their specific fields and units
export const categoryConfig = {
  bom: {
    label: 'Bill of Materials',
    icon: Package,
    color: 'bg-blue-500/20 text-blue-600 border-blue-500/30',
    bgColor: 'bg-blue-50',
    columns: [
      { key: 'name', label: 'Material/Component', type: 'dataset-search', placeholder: 'Search dataset...', required: true },
      { key: 'quantity', label: 'Quantity', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'unit', label: 'Unit', type: 'select', options: ['kg', 'pcs', 'm²', 'm', 'L', 'g'], required: true },
      { key: 'geography', label: 'Geography', type: 'text', placeholder: 'Global' },
      { key: 'quality', label: 'Quality', type: 'quality' }
    ]
  },
  energy: {
    label: 'Energy Inputs',
    icon: Zap,
    color: 'bg-amber-500/20 text-amber-600 border-amber-500/30',
    bgColor: 'bg-amber-50',
    columns: [
      { key: 'name', label: 'Energy Source', type: 'dataset-search', placeholder: 'Search dataset...', required: true },
      { key: 'quantity', label: 'Quantity', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'unit', label: 'Unit', type: 'select', options: ['kWh', 'MJ', 'm³', 'L', 'kg'], required: true },
      { key: 'geography', label: 'Geography', type: 'text', placeholder: 'Global' },
      { key: 'quality', label: 'Quality', type: 'quality' }
    ]
  },
  transport: {
    label: 'Transport',
    icon: Truck,
    color: 'bg-emerald-500/20 text-emerald-600 border-emerald-500/30',
    bgColor: 'bg-emerald-50',
    columns: [
      { key: 'name', label: 'Transport Type', type: 'dataset-search', placeholder: 'Search dataset...', required: true },
      { key: 'distance_km', label: 'Distance (km)', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'load_kg', label: 'Load (kg)', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'geography', label: 'Geography', type: 'text', placeholder: 'Global' },
      { key: 'quality', label: 'Quality', type: 'quality' }
    ]
  },
  process: {
    label: 'Processes',
    icon: Settings,
    color: 'bg-purple-500/20 text-purple-600 border-purple-500/30',
    bgColor: 'bg-purple-50',
    columns: [
      { key: 'name', label: 'Process Name', type: 'dataset-search', placeholder: 'Search dataset...', required: true },
      { key: 'quantity', label: 'Quantity', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'unit', label: 'Unit', type: 'select', options: ['m', 'kg', 'm²', 'hours', 'pcs'], required: true },
      { key: 'geography', label: 'Geography', type: 'text', placeholder: 'Global' },
      { key: 'quality', label: 'Quality', type: 'quality' },
      { key: 'yield_percent', label: 'Yield %', type: 'number', placeholder: '100', align: 'right' },
      { key: 'scrap_percent', label: 'Scrap %', type: 'number', placeholder: '0', align: 'right' }
    ]
  }
};

// Gate-to-Grave category configurations (conditionally shown)
export const g2gCategoryConfig = {
  use_phase: {
    label: 'Use Phase',
    icon: Activity,
    color: 'bg-cyan-500/20 text-cyan-600 border-cyan-500/30',
    bgColor: 'bg-cyan-50',
    columns: [
      { key: 'name', label: 'Resource/Consumable', type: 'dataset-search', placeholder: 'Search dataset...', required: true },
      { key: 'quantity', label: 'Quantity', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'unit', label: 'Unit', type: 'select', options: ['kWh', 'L', 'm³', 'kg', 'pcs'], required: true },
      { key: 'usage_basis', label: 'Per', type: 'select', options: ['per_year', 'per_hour', 'per_cycle'], required: true },
      { key: 'geography', label: 'Geography', type: 'text', placeholder: 'Global' },
      { key: 'quality', label: 'Quality', type: 'quality' }
    ]
  },
  maintenance: {
    label: 'Maintenance & Replacement',
    icon: Wrench,
    color: 'bg-orange-500/20 text-orange-600 border-orange-500/30',
    bgColor: 'bg-orange-50',
    columns: [
      { key: 'name', label: 'Spare Part/Component', type: 'dataset-search', placeholder: 'Search dataset...', required: true },
      { key: 'component_name', label: 'Component', type: 'text', placeholder: 'e.g., Battery, Filter' },
      { key: 'quantity', label: 'Qty per Replacement', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'unit', label: 'Unit', type: 'select', options: ['kg', 'pcs', 'L', 'm'], required: true },
      { key: 'replacement_interval_years', label: 'Interval (years)', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'quality', label: 'Quality', type: 'quality' }
    ]
  },
  eol: {
    label: 'End-of-Life',
    icon: Recycle,
    color: 'bg-rose-500/20 text-rose-600 border-rose-500/30',
    bgColor: 'bg-rose-50',
    columns: [
      { key: 'name', label: 'Process/Treatment', type: 'dataset-search', placeholder: 'Search dataset...', required: true },
      { key: 'quantity', label: 'Quantity', type: 'number', placeholder: '0', required: true, align: 'right' },
      { key: 'unit', label: 'Unit', type: 'select', options: ['kg', 'tkm', 'pcs'], required: true },
      { key: 'eol_method', label: 'Method', type: 'select', options: ['cut_off', 'substitution', 'allocation'] },
      { key: 'geography', label: 'Geography', type: 'text', placeholder: 'Global' },
      { key: 'quality', label: 'Quality', type: 'quality' }
    ]
  }
};

export const qualityOptions = ['high', 'medium', 'proxy'];

export function getCategoryConfig(cat) {
  if (categoryConfig[cat]) return categoryConfig[cat];
  if (g2gCategoryConfig[cat]) return g2gCategoryConfig[cat];
  return categoryConfig.bom;
}

export function getEmptyRow(category) {
  const config = getCategoryConfig(category);
  const baseRow = {
    name: '',
    dataset_id: null,
    emission_factor: null,
    quantity: '',
    unit: config.columns.find(c => c.key === 'unit')?.options?.[0] || '',
    geography: 'Global',
    data_quality: 'medium',
    distance_km: '',
    load_kg: '',
    yield_percent: '',
    scrap_percent: ''
  };

  // Add G2G specific fields
  if (category === 'use_phase') {
    baseRow.usage_basis = 'per_year';
  } else if (category === 'maintenance') {
    baseRow.component_name = '';
    baseRow.replacement_interval_years = '';
  } else if (category === 'eol') {
    baseRow.eol_method = 'cut_off';
  }

  return baseRow;
}
